/**
 * ============================================================================
 *  CONSTANTS — Dynamic Form / App Business Requirement
 * ============================================================================
 *
 * File này CHỈ chứa các constant/enum thuần (không có Zod schema, không có
 * suy diễn type động). Mục đích: tách layer "dữ liệu hằng số" khỏi schema
 * (`./schema.ts`) và type (`./types.ts`) để dễ bảo trì.
 *
 * Tham khảo barrel `./index.ts` để import gọn từ các nơi khác.
 */

/* -------------------------------------------------------------------------- */
/*                       Loại nghiệp vụ (Validate / Dict)                       */
/* -------------------------------------------------------------------------- */

/**
 * Phân loại nghiệp vụ AppBusinessRequirement của hệ thống:
 * - `Dict`     => dùng để lấy dữ liệu (từ điển).
 * - `Validate` => dùng để kiểm tra điều kiện (true/false).
 */
export enum AppBusinessRequirementType {
    Dict,
    Validate,
}

/* -------------------------------------------------------------------------- */
/*                Mã nghiệp vụ KIỂM TRA (Requirement / Validate)                */
/* -------------------------------------------------------------------------- */

/**
 * Mã nghiệp vụ dùng để KIỂM TRA điều kiện (trả về boolean true/false).
 *
 * Quy ước đặt tên:
 * - `*_YES` => Yêu cầu thoả mãn điều kiện (executor backend trả về `true`).
 * - `*_NO`  => Yêu cầu KHÔNG thoả mãn (executor backend trả về `false`).
 *
 * Cùng 1 executor backend (vd: `UIS_KiemTraHoanTatHocPhi`) có thể được dùng
 * theo cả 2 chiều `_YES`/`_NO` tuỳ cấu hình của thủ tục.
 */
export const APP_BUSINESS_REQUIREMENT_CODES = {
    og_kiem_tra_gioi_tinh_nam_YES: 'OG_KiemTraGioiTinhNam',
    og_kiem_tra_gioi_tinh_nam_NO: 'OG_NOT_KiemTraGioiTinhNam',
    og_kiem_tra_gioi_tinh_nu_YES: 'OG_KiemTraGioiTinhNu',
    og_kiem_tra_gioi_tinh_nu_NO: 'OG_NOT_KiemTraGioiTinhNu',
    uis_kiem_tra_dang_ky_nghi_dinh_116_tien_hoc_phi_sinh_hoat_phi_NO:
        'UIS_NOT_KiemTraDangKyNghiDinh116TienHocPhiSinhHoatPhi',
    uis_kiem_tra_dang_ky_nghi_dinh_116_tien_hoc_phi_sinh_hoat_phi_YES:
        'UIS_KiemTraDangKyNghiDinh116TienHocPhiSinhHoatPhi',
    uis_kiem_tra_hoan_tat_hoc_phi_NO: 'UIS_NOT_KiemTraHoanTatHocPhi',
    uis_kiem_tra_hoan_tat_hoc_phi_YES: 'UIS_KiemTraHoanTatHocPhi',
    /** Kiểm tra trạng thái sinh viên: Còn học / Đã thôi học */
    uis_kiem_tra_thong_tin_sinh_vien_YES: 'UIS_KiemTraThongTinSinhVien',
} as const;

/* -------------------------------------------------------------------------- */
/*                  Mã nghiệp vụ TỪ ĐIỂN (Dictionary / Dict)                    */
/* -------------------------------------------------------------------------- */

/**
 * Mã nghiệp vụ dùng để LẤY DỮ LIỆU (trả về danh sách / object).
 * Khác với REQUIREMENT_CODES (trả về boolean để kiểm tra điều kiện).
 */
export const APP_BUSINESS_DICTIONARY_CODES = {
    /** Lấy danh sách chương trình đào tạo SV đã đăng ký */
    uis_chuong_trinh_dao_tao_cua_sinh_vien: 'UIS_ChuongTrinhDaoTaoCuaSinhVien',
    /** Lấy thông tin SV theo chương trình đào tạo */
    uis_thong_tin_sinh_vien_theo_chuong_trinh_dao_tao:
        'UIS_ThongTinSinhVienTheoChuongTrinhDaoTao',
    /** Lấy danh sách tiến độ đào tạo của SV */
    uis_tien_do_dao_tao_cua_sinh_vien: 'UIS_TienDoDaoTaoCuaSinhVien',
    /** Lấy từ điển trạng thái SV (Còn học/Thôi học…) */
    uis_trang_thai_sinh_vien: 'UIS_TrangThaiSinhVien',
    uis_chuong_trinh_dao_tao_theo_khoa_moi_nhat:
        'UIS_ChuongTrinhDaoTaoTheoKhoaMoiNhat',
    uis_loai_hinh_dao_tao: 'UIS_LoaiHinhDaoTao',
    uis_hinh_thuc_dao_tao_tai_truong: 'HinhThucDaoTaoTaiTruong',
    uis_chuong_trinh_dao_tao: 'UIS_ChuongTrinhDaoTao',
    uis_lop_theo_chuong_trinh_dao_tao: 'UIS_LopTheoChuongTrinhDaoTao',
    /** Thông tin đăng ký Nghị định 116 + số tiền học phí/sinh hoạt phí đã nhận */
    uis_dang_ky_nghi_dinh_116tien_hoc_phi_sinh_hoat_phi:
        'UIS_DangKyNghiDinh116TienHocPhiSinhHoatPhi',
    uis_loai_sinh_vien: 'UIS_LoaiSinhVien',
    uis_dia_danh_hanh_chinh: 'UIS_DiaDanhHanhChinh',
    uis_khoa_tuyen_sinh_sinh_vien: 'UIS_KhoaTuyenSinhSinhVien',
    /** Lấy số tiền học phí chưa hoàn tất (danh sách chi tiết các khoản nợ) */
    uis_lay_so_tien_hoc_phi_chua_duoc_hoan_tat:
        'UIS_LaySoTienHocPhiChuaDuocHoanTat',
    /**
     * Thời gian nghỉ học — danh sách học kỳ SV có thể đăng ký nghỉ.
     *
     * Đây là mã DICTIONARY phía CLIENT (virtual): không có executor backend riêng.
     * Component đặc thù trong registry sẽ gọi `uis_tien_do_dao_tao_cua_sinh_vien`
     * để lấy DsHocKyConLai rồi map thành options.
     *
     * THAY THẾ cho cơ chế cũ `MAIL_MERGE_KEYs.ThoiGianNghiHoc` (đã gỡ bỏ) — vốn
     * dùng logic hardcode `onHandleThoiGianTamNghi` để tính & ghi mô tả.
     */
    uis_thoi_gian_nghi_hoc: 'UIS_ThoiGianNghiHoc',

    /* ---------------------------------------------------------------------- */
    /*           SNAPSHOT DICTIONARIES — chốt dữ liệu khi NỘP hồ sơ           */
    /* ---------------------------------------------------------------------- */
    /*
     * 4 mã DICTIONARY VIRTUAL bên dưới đại diện cho các hàm map mail merge
     * trong `custom-modal-print-decision/mapping.ts` (mapBieuMauTamNghiHoc,
     * mapBieuMauXinThoiHoc, mapBieuMauXinVaoHoc, mapHeDaoTaoAndNamTS).
     *
     * Lý do tách thành dictionary: dữ liệu phụ thuộc TIẾN ĐỘ ĐÀO TẠO tại THỜI
     * ĐIỂM NỘP hồ sơ — nếu để mỗi lần in mới tính lại thì giá trị có thể đổi
     * (sinh viên đã qua học kỳ khác). Component Field tương ứng sẽ:
     *   1. Gọi map function với context hiện tại (tienDoDaoTao + form values).
     *   2. Serialize `MailMergeRecord[]` vào `question.value` (JSON).
     *   3. Hiển thị tóm tắt read-only để người dùng nhìn thấy giá trị đã chốt.
     *
     * Khi in: `mapExtendedInfoToRecord` đọc snapshot JSON này và phát ra
     * thẳng các record — KHÔNG gọi lại map function với dữ liệu live nữa.
     */
    /** Snapshot "biểu mẫu tạm nghỉ học" — From/To HocKy, NamHoc */
    uis_map_bieu_mau_tam_nghi_hoc: 'UIS_MapBieuMauTamNghiHoc',
    /** Snapshot "biểu mẫu xin thôi học" — From HocKy, NamHoc hiện tại */
    uis_map_bieu_mau_xin_thoi_hoc: 'UIS_MapBieuMauXinThoiHoc',
    /** Snapshot "biểu mẫu xin vào học lại" — From HocKy, NamHoc xin vào */
    uis_map_bieu_mau_xin_vao_hoc: 'UIS_MapBieuMauXinVaoHoc',
    /** Snapshot "hệ đào tạo + năm TS" — khóa học, năm tuyển sinh, học kỳ hiện tại */
    uis_map_he_dao_tao_va_nam_ts: 'UIS_MapHeDaoTaoVaNamTS',

    /**Danh sách điểm rèn luyện theo sinh viên */
    uis_diem_ren_luyen_sinh_vien_theo_hoc_ky: 'UIS_DiemRenLuyenSinhVienTheoHocKy',
    /**danh sách các học kỳ mà sinh viên có điểm rèn luyện */
    uis_danh_sach_hoc_ky_co_diem_ren_luyen_cua_sinh_vien: 'UIS_StudentConductScoreSemesters',
    /**danh sách các học kỳ mà sinh viên có điểm rèn luyện */
    uis_danh_sach_cac_loai_chung_chi: 'UIS_DanhSachCacLoaiChungChi',
    /**Lấy danh sách dịch vụ mở đăng ký (bảng điểm...) của sinh viên */
    uis_lay_thong_tin_dich_vu_bang_diem: 'UIS_LayThongTinDichVuBangDiem',
    /**Lấy danh sách năm học - chưa dùng*/
    uis_lay_danh_sach_nam_hoc: 'UIS_LayDanhSachNamHoc',
    /**Lấy danh sách học kỳ - chưa dùng*/
    uis_lay_danh_sach_hoc_ky: 'UIS_LayDanhSachHocKy',
    /**Lấy danh sách năm học dịch vụ*/
    uis_lay_danh_sach_nam_hoc_dich_vu: 'UIS_LayDanhSachNamHocDichVu',
    /**
     * Lấy danh sách môn học của dịch vụ (VD: bảng điểm chi tiết cần chọn môn).
     * Params: `StudentID`, `StudyProgramID` — `StudyProgramID` LẤY TỪ `MaChuongTrinh`
     * của lựa chọn ở câu hỏi dùng `uis_lay_thong_tin_dich_vu_bang_diem` (xem
     * `leaveRequestInfo.maChuongTrinh`, ghi bởi `render-option-of-question.tsx`).
     */
    uis_get_all_mon_hoc_dich_vu: 'UIS_GetAllMonHocDichVu',

    /**HRM */
    hrm_thong_tin_buoi_trong_ngay: 'HRM_ThongTinBuoiTrongNgay',
    hrm_thong_tin_loai_che_do: 'HRM_ThongTinLoaiCheDo', //Không dùng, do đang dùng HRM_GetDayDuThongTinLoaiNghiPhep
    hrm_thong_tin_loai_nghi_phep: 'HRM_ThongTinLoaiNghiPhep', //Không dùng, do đang dùng HRM_GetDayDuThongTinLoaiNghiPhep
    hrm_get_day_du_thong_tin_loai_nghi_phep: 'HRM_GetDayDuThongTinLoaiNghiPhep',
    /**
     * Lấy thông tin giảng viên/cán bộ (họ tên, giới tính, chức vụ, đơn vị,
     * phân loại...) theo mã nhân sự. Param: `MaNhanSu` = `CivilServantCode`
     * trong thông tin tài khoản cán bộ.
     */
    hrm_get_thong_tin_giang_vien: 'HRM_GetThongTinGiangVien',
    /**
     * Kiểm tra thông tin phép (số ngày đã xin / số phép còn lại...) của 1 cán bộ.
     * Param: `maNhanSu` = `CivilServantCode` trong thông tin tài khoản cán bộ
     * (`auth?.ExtraInfo?.UserInfo?.CivilServantCode`) — KHÔNG dùng StudentID.
     */
    hrm_kiem_tra_thong_tin_phep: 'HRM_KiemTraThongTinPhep',
    /**
     * Tính số ngày đăng ký nghỉ theo khoảng ngày + buổi đã chọn.
     * Params: `NgayBatDau`/`NgayKetThuc` ("yyyy-MM-dd"), `Buoi_TuNgay`/
     * `Buoi_DenNgay` (Oid của từ điển `hrm_thong_tin_buoi_trong_ngay`).
     * Response: 1 số (số ngày).
     */
    hrm_tinh_so_ngay_dang_ky_nghi: 'HRM_TinhSoNgayDangKyNghi',
    /**
     * Lấy SỐ NGÀY NGHỈ TỐI ĐA được đăng ký cho 1 cặp (hình thức nghỉ, chế độ
     * nghỉ) của 1 cán bộ. Params: `TuNgay` (ISO — thời điểm gửi/00:00 giờ VN),
     * `HinhThucNghi` (= `selectedKey` câu hỏi nâng cao
     * `HRM_GetDayDuThongTinLoaiNghiPhep`), `LoaiCheDo` (value field con select),
     * `maQuanLy` (`UserInfo.CivilServantCode`).
     * Response: 1 số (số ngày tối đa) — xem `use-query-lay-so-ngay-nghi-toi-da`.
     */
    hrm_lay_so_ngay_nghi_toi_da: 'HRM_LaySoNgayNghiToiDa',
    /**
     * Kiểm tra thông tin tạo phép bên HRM TRƯỚC khi nộp hồ sơ "Xin nghỉ phép".
     * Chỉ gọi khi form có câu hỏi nâng cao `HRM_GetDayDuThongTinLoaiNghiPhep`.
     * Params: `TuNgay`/`DenNgay` (ISO — 00:00 giờ VN của ngày BD/KT nghỉ),
     * `DienGiai`/`diaChiLienHe`/`noiNghi` (chuỗi rỗng), `idHinhThucNghi`,
     * `idLoaiCheDo`, `cacBuoiTrongNgay_TuNgay`/`cacBuoiTrongNgay_DenNgay` (Oid
     * buổi), `maQuanLy` (`UserInfo.CivilServantCode`).
     * Response: object `{ Success, Message }` — `Success !== true` ⇒ chặn nộp.
     */
    hrm_kiem_tra_thong_tin_tao_phep: 'HRM_KiemTraThongTinTaoPhep',
    /**Value của Execute dùng để lấy cây phòng ban/người dùng bên hệ thống E-Office — không cần Params. */
    eo_get_department_and_users: 'EO_GetDepartmentAndUsers',
} as const;

/* -------------------------------------------------------------------------- */
/*       Mã DICTIONARY "CHỌN ĐƯỢC" — hiển thị cho người dùng tự chọn options    */
/* -------------------------------------------------------------------------- */

/**
 * Tập con của `APP_BUSINESS_DICTIONARY_CODES` chỉ gồm các mã THỰC SỰ render ra
 * danh sách options để người dùng CHỌN trong câu hỏi loại `directories`
 * ("Từ điển — Chọn từ điển dữ liệu").
 *
 * Tiêu chí "chọn được":
 * - Có nhánh ánh xạ trong `mapDirectoriesToOptions` (schema.ts) → ra options từ API, HOẶC
 * - Có component `Field` riêng cho phép CHỌN (vd `UIS_ThoiGianNghiHoc` → select học kỳ).
 *
 * KHÔNG bao gồm:
 * - 4 mã snapshot `uis_map_*` (Field read-only, không chọn).
 * - Các mã chỉ dùng để LẤY DỮ LIỆU lập trình (tiến độ đào tạo, thông tin SV theo CTĐT,
 *   học phí chưa hoàn tất, đăng ký NĐ116, trạng thái SV cấu hình…).
 * - Các mã "danh mục" đã đăng ký registry NHƯNG chưa có `mapDirectoriesToOptions`
 *   (chuong_trinh_dao_tao, loai_sinh_vien, dia_danh_hanh_chinh, khoa_tuyen_sinh_sinh_vien)
 *   → render rỗng nên hiện chưa cho chọn; bổ sung sau khi có hàm map.
 *
 * CHỈ dùng hằng số này để lọc danh sách hiển thị ở chỗ "Chọn từ điển dữ liệu".
 */
export const APP_BUSINESS_DICTIONARY_SELECTABLE_CODES = {
    uis_chuong_trinh_dao_tao_theo_khoa_moi_nhat:
        APP_BUSINESS_DICTIONARY_CODES.uis_chuong_trinh_dao_tao_theo_khoa_moi_nhat,
    uis_loai_hinh_dao_tao: APP_BUSINESS_DICTIONARY_CODES.uis_loai_hinh_dao_tao,
    uis_hinh_thuc_dao_tao_tai_truong:
        APP_BUSINESS_DICTIONARY_CODES.uis_hinh_thuc_dao_tao_tai_truong,
    uis_lop_theo_chuong_trinh_dao_tao:
        APP_BUSINESS_DICTIONARY_CODES.uis_lop_theo_chuong_trinh_dao_tao,
    uis_thoi_gian_nghi_hoc:
        APP_BUSINESS_DICTIONARY_CODES.uis_thoi_gian_nghi_hoc,
    /**Bổ sung */
    uis_diem_ren_luyen_sinh_vien_theo_hoc_ky:
        APP_BUSINESS_DICTIONARY_CODES.uis_diem_ren_luyen_sinh_vien_theo_hoc_ky,
    uis_danh_sach_hoc_ky_co_diem_ren_luyen_cua_sinh_vien:
        APP_BUSINESS_DICTIONARY_CODES.uis_danh_sach_hoc_ky_co_diem_ren_luyen_cua_sinh_vien,
    uis_danh_sach_cac_loai_chung_chi:
        APP_BUSINESS_DICTIONARY_CODES.uis_danh_sach_cac_loai_chung_chi,
    uis_lay_thong_tin_dich_vu_bang_diem: APP_BUSINESS_DICTIONARY_CODES.uis_lay_thong_tin_dich_vu_bang_diem,
    uis_lay_danh_sach_nam_hoc_dich_vu: APP_BUSINESS_DICTIONARY_CODES.uis_lay_danh_sach_nam_hoc_dich_vu,
    uis_get_all_mon_hoc_dich_vu: APP_BUSINESS_DICTIONARY_CODES.uis_get_all_mon_hoc_dich_vu,

    /**HRM */
    hrm_thong_tin_buoi_trong_ngay: APP_BUSINESS_DICTIONARY_CODES.hrm_thong_tin_buoi_trong_ngay,
    // hrm_thong_tin_loai_che_do: APP_BUSINESS_DICTIONARY_CODES.hrm_thong_tin_loai_che_do,
    // hrm_thong_tin_loai_nghi_phep: APP_BUSINESS_DICTIONARY_CODES.hrm_thong_tin_loai_nghi_phep,
    hrm_get_day_du_thong_tin_loai_nghi_phep: APP_BUSINESS_DICTIONARY_CODES.hrm_get_day_du_thong_tin_loai_nghi_phep,
} as const;

/* -------------------------------------------------------------------------- */
/*                          Tổng hợp toàn bộ mã nghiệp vụ                       */
/* -------------------------------------------------------------------------- */

/**
 * Gộp REQUIREMENT_CODES + DICTIONARY_CODES.
 * Dùng khi cần đối chiếu/validate một chuỗi bất kỳ với toàn bộ mã hệ thống.
 */
export const APP_BUSINESS_CODES = {
    ...APP_BUSINESS_REQUIREMENT_CODES,
    ...APP_BUSINESS_DICTIONARY_CODES,
} as const;

/* -------------------------------------------------------------------------- */
/*                       Tham số khi gọi executor (Params)                      */
/* -------------------------------------------------------------------------- */

/**
 * Danh sách các params mà APP_BUSINESS có thể cần truyền khi gọi executor.
 *
 * Nguồn: đọc các interface query của executor backend
 * (`be/Projects/PSC.UIS/Executors/*.cs` → `IStudentID`, `IStudyProgramID`, `ICourseID`
 *  định nghĩa tại `be/CORE/src/Domain.Core/Interface/*.cs`).
 *
 * Mỗi value khớp ĐÚNG tên property mà backend `FromJSONTo<Query>()` mong đợi
 * trong `BusinessExecutorRequest.Parameters`.
 */
export enum AppBusinessRequirementParams {
    /** Mã số sinh viên — IStudentID.StudentID */
    StudentID = 'StudentID',
    /** Mã chương trình đào tạo — IStudyProgramID.StudyProgramID */
    StudyProgramID = 'StudyProgramID',
    /** Mã khóa học — ICourseID.CourseID */
    CourseID = 'CourseID',
}

/* -------------------------------------------------------------------------- */
/*                           Loại câu hỏi của Form động                         */
/* -------------------------------------------------------------------------- */

export enum QuestionType {
    /** Trả lời ngắn */
    text = 'text',
    /** Đoạn văn bản */
    textarea = 'textarea',
    select = 'select',
    checkbox = 'checkbox',
    radio = 'radio',
    number = 'number',
    /** Input date */
    date = 'date',
    /** Từ điển — gọi API để lấy options */
    directories = 'directories',
    /** Tìm kiếm theo từ khoá */
    select_hv_search = 'select_hv_search',
    /**
     * Danh sách nhóm dữ liệu — mỗi phần tử là 1 object gồm nhiều field con.
     * Lưu trong RHF dạng JSON string. Không hỗ trợ lồng arrayObject vào nhau.
     */
    arrayObject = 'arrayObject',
    /**
     * NÂNG CAO — trước đây chỉ bật ở dev, nay đã mở khoá cho production (xem
     * `question.tsx` — `QUESTION_TYPE_ENTRIES`) vì có nghiệp vụ thật dùng
     * (VD: HRM_GetDayDuThongTinLoaiNghiPhep).
     *
     * Render UI giống `arrayObject` (danh sách nhóm field con), NHƯNG định
     * nghĩa field con KHÔNG cấu hình tĩnh — fetch ĐỘNG từ
     * `appBusinessRequirement.Execute({Value: api_key})` (api_key = từ điển
     * chọn qua `ModalConfigApiHandle`, giống hệt `directories`). Dùng khi 1
     * từ điển backend trả sẵn cấu trúc form thay vì admin phải tự khai field.
     *
     * Field con fetch được sẽ SNAPSHOT vào `arrayObjectConfig.fields` lúc
     * runtime (xem `RenderAdvancedDynamicFields`) để pipeline mail-merge/in
     * quyết định tái sử dụng nguyên switch-case của `arrayObject`.
     */
    advanced = 'advanced',
}

/* -------------------------------------------------------------------------- */
/*              Toán tử điều kiện cho "Mô tả hiển thị có điều kiện"             */
/* -------------------------------------------------------------------------- */

/**
 * Toán tử so sánh giá trị từ khoá đục lỗ với cấu hình admin nhập.
 * Dùng trong `conditionalDescriptions.items[].conditions[]`.
 *
 * - `hasValue` / `noValue` : kiểm tra key có/không có giá trị (không cần `value`).
 * - `equal` / `notEqual`   : so sánh chuỗi.
 * - `regex`                : value là pattern regex, test trên giá trị thực.
 * - `gt` / `lt`            : so sánh SỐ (parse Number 2 vế).
 */
export enum ConditionOperator {
    HasValue = 'hasValue',
    NoValue = 'noValue',
    Equal = 'equal',
    NotEqual = 'notEqual',
    Regex = 'regex',
    GreaterThan = 'gt',
    LessThan = 'lt',
}

/* -------------------------------------------------------------------------- */
/*                          Mail merge keys mặc định                            */
/* -------------------------------------------------------------------------- */

/*
 * ĐÃ GỠ BỎ enum `MAIL_MERGE_KEYs` (chỉ chứa 1 member `ThoiGianNghiHoc`).
 *
 * Lý do: cơ chế cũ xử lý `ThoiGianNghiHoc` bằng logic hardcode
 * (`onHandleThoiGianTamNghi` trong answer.tsx) đã được thay thế bởi:
 *   1. Cơ chế "Hiển thị có điều kiện" (conditionalDescriptions) — generic placeholder.
 *   2. Dictionary mới `APP_BUSINESS_DICTIONARY_CODES.uis_thoi_gian_nghi_hoc`
 *      + component đặc thù trong registry.
 *
 * `mail_merge_key` của câu hỏi nay chỉ còn kiểu `string` (xem schema.ts) —
 * admin tự nhập/chọn key tuỳ ý, không còn enum cứng.
 */

/**
 * Key dùng để lấy thông tin người tiếp nhận online.
 */
export const DYNAMIC_KEY_RECEIVER_ONLINE = 'online_receiver';

/* -------------------------------------------------------------------------- */
/*               Text báo lỗi mặc định (legacy — server action)                 */
/* -------------------------------------------------------------------------- */

/**
 * Text báo lỗi mặc định khi nghiệp vụ kiểm tra KHÔNG thoả mãn.
 *
 * @deprecated Phía client cust-dossier dùng business-rule service mới
 * (`@/lib/business-rules`) trả về `{code, passed}`, text được resolve qua i18n
 * trên client. Constant này CHỈ còn dùng cho server action legacy
 * `checkStudentBusinessRequirements` phục vụ trang dossier-procedure-record.
 * TODO: chuyển trang đó sang dùng service mới rồi xoá hằng số này.
 */
export const AppBusinessRequirementErrorText: Partial<Record<string, string>> =
    {
        [APP_BUSINESS_REQUIREMENT_CODES.uis_kiem_tra_hoan_tat_hoc_phi_YES]:
            'Hiện tại SV chưa hoàn tất học phí nên không thể đăng ký thủ tục. SV cần thực hiện đóng học phí sớm. Nếu đã hoàn tất học phí nhưng chưa được trừ nợ, SV vui lòng liên hệ phòng Kế hoạch - Tài chính.',
        [APP_BUSINESS_REQUIREMENT_CODES.uis_kiem_tra_dang_ky_nghi_dinh_116_tien_hoc_phi_sinh_hoat_phi_YES]:
            'SV có đăng ký tham gia theo Nghị định 116/2020/NĐ-CP, nếu thôi học, SV cần bồi hoàn toàn bộ học phí và sinh hoạt phí đã được hưởng.',
        [APP_BUSINESS_REQUIREMENT_CODES.uis_kiem_tra_thong_tin_sinh_vien_YES]:
            'Trạng thái sinh viên không được phép đăng ký hồ sơ. Vui lòng kiểm tra lại!',
    };
